// "Abonar bono manual": un ajuste en el corte en curso del vendedor, con motivo para la auditoría.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { useState } from 'react';
import { toast } from 'sonner';
import { userMessageOf } from '@/api/errors';
import { Button, Input, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import {
    isAdjustmentKeyReused,
    isPayoutNotOpen,
    useAdjustmentDoubt,
    useCreatePayoutAdjustment,
} from '../../api/commissionQueries';
import { validateAdjustment, type AdjustmentErrors } from '../../lib/adjustmentForm';
import { localToday } from '../../lib/payoutPeriods';
import { centsToPesos, pesosToCents, typedPesos } from '../../lib/pesosInput';
import { FIELD, FIELD_ERROR } from '../fieldStyles';
import { doubtModeOf, WriteDoubtNotice } from './WriteDoubtNotice';

const KEY_REUSED =
    'Ya se había registrado un ajuste de este vendedor en el corte; revisa su comisión actualizada.';

interface AdjustmentModalProps {
    sellerId: string;
    sellerName: string;
    /** El corte en curso: uno cerrado ya está sellado. */
    periodId: string;
    onClose: () => void;
}

export function AdjustmentModal({ sellerId, sellerName, periodId, onClose }: AdjustmentModalProps) {
    // Tras un fallo sin respuesta clara se reintenta con la misma llave. Lo enviado completo solo lo
    // conoce esta pestaña; tras recargar o en otra, el monto viene precargado y el motivo se escribe.
    const { doubt, expired, settle } = useAdjustmentDoubt(periodId, sellerId);
    const [editing, setEditing] = useState(false);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<AdjustmentErrors>({});
    const [notOpen, setNotOpen] = useState<string | null>(null);
    const sent = doubt?.body ?? null;
    const recapture = doubt !== null && sent === null;

    // Una duda sin lo enviado obliga a capturar: monto precargado y motivo vacío.
    const [recapturedKey, setRecapturedKey] = useState<string | null>(null);
    if (recapture && recapturedKey !== doubt.key) {
        setRecapturedKey(doubt.key);
        setAmount(doubt.amountCents === null ? '' : centsToPesos(doubt.amountCents));
        setReason('');
    }

    // Cerrar el formulario cierra el intento, salvo con la duda viva.
    const finish = () => {
        settle();
        onClose();
    };

    // Los avisos van en la mutación: corren aunque el formulario ya se haya cerrado.
    const create = useCreatePayoutAdjustment(periodId, {
        onSuccess: () => {
            toast.success('Ajuste registrado', {
                description: `Entra al corte en curso de ${sellerName}.`,
            });
            finish();
        },
        onError: (error) => {
            if (isAdjustmentKeyReused(error)) {
                // Lo registrado con esa llave ya movió el corte: no se deja el mismo envío listo.
                toast.error(KEY_REUSED);
                finish();
                return;
            }
            setEditing(false);
            if (isPayoutNotOpen(error)) setNotOpen(userMessageOf(error));
            else toast.error(userMessageOf(error));
        },
    });
    const retrySent = sent !== null && !editing;
    const locked = retrySent || create.isPending;
    const mode = doubtModeOf({
        inDoubt: doubt !== null,
        busy: create.isPending || doubt?.sendingHere === true,
        elsewhere: doubt?.inFlightElsewhere === true,
        recapture,
        editing,
    });

    const cancel = () => {
        if (!create.isPending) finish();
    };

    const editSent = () => {
        if (sent === null) return;
        setAmount(centsToPesos(sent.amountCents));
        setReason(sent.reason);
        setErrors({});
        setEditing(true);
    };

    const submit = () => {
        setNotOpen(null);
        if (retrySent) {
            create.mutate({ sellerId, body: sent, shown: doubt });
            return;
        }
        const found = validateAdjustment(amount, reason);
        setErrors(found);
        const cents = pesosToCents(amount);
        if (found.amount !== undefined || found.reason !== undefined || cents === null) return;
        create.mutate({
            sellerId,
            body: {
                sellerId,
                amountCents: cents,
                reason: reason.trim(),
                saleId: null,
                date: localToday(),
            },
            shown: doubt,
        });
    };

    return (
        <Modal isOpen onClose={cancel} title={`Abonar bono manual · ${sellerName}`} size="sm">
            <form
                noValidate
                className="flex flex-col gap-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <Input
                    id="adj-amount"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    label="Monto (MXN)"
                    placeholder="ej. 200"
                    hint="Entra al corte en curso. Un monto negativo descuenta."
                    value={retrySent ? centsToPesos(sent.amountCents) : amount}
                    disabled={locked}
                    onChange={(event) => {
                        setAmount(typedPesos(event.target.value, true));
                    }}
                    error={errors.amount}
                />
                <div>
                    <label htmlFor="adj-reason" className="text-sm font-medium text-foreground">
                        Motivo
                    </label>
                    <textarea
                        id="adj-reason"
                        rows={3}
                        maxLength={500}
                        required
                        className={cn(FIELD, 'mt-1 h-auto py-1')}
                        placeholder="Detalle para auditoría"
                        aria-invalid={errors.reason ? true : undefined}
                        aria-describedby={errors.reason ? 'adj-reason-error' : undefined}
                        value={retrySent ? sent.reason : reason}
                        disabled={locked}
                        onChange={(event) => {
                            setReason(event.target.value);
                        }}
                    />
                    {errors.reason !== undefined && (
                        <p id="adj-reason-error" className={FIELD_ERROR}>
                            {errors.reason}
                        </p>
                    )}
                </div>
                <WriteDoubtNotice
                    mode={mode}
                    expired={expired}
                    disabled={create.isPending}
                    onEdit={editSent}
                />
                {notOpen !== null && (
                    <p role="alert" className={FIELD_ERROR}>
                        {notOpen}
                    </p>
                )}
                <div className="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={create.isPending}
                        onClick={cancel}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={create.isPending}>
                        {retrySent ? 'Reintentar' : 'Abonar'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
